import type {
  DirectedVideoClipEntry,
  DirectedVideoStatus,
  SerializedDirectedVideo,
} from 'fym-shared/src/music24/directedVideoApi';

/** The state of one shot tile of the make (design g1, g2). */
export type ProgressTileState = 'ready' | 'failed' | 'making' | 'queued';

/** One tile of the make: one Veo clip, numbered by its place in the clip order. */
export interface ProgressTile {
  key: string;
  state: ProgressTileState;
  /**
   * The picture of the tile: the still of a ready clip (its poster), else the keyframe of the clip
   * (the first frame that Veo animates), also while Veo makes it. Null for a failed clip and a clip
   * with neither. Never the cover art of the song.
   */
  stillUrl: string | null;
}

/**
 * The clips that Veo makes at one time (the `AI_REELS_VEO_CONCURRENCY` of the stacks, design g1
 * "3 at a time"). The video answer has no state per clip, so the tiles after the done ones show
 * this many as "making" and the rest as queued.
 */
export const DIRECTED_CLIPS_AT_ONCE = 3;

/**
 * The tiles of the make, in order: the made shots, the failed shots, the shots in the making, the
 * queued shots. The video answer counts the clips, it does not order them. Pure.
 */
export function progressTiles(
  status: DirectedVideoStatus,
  total: number,
  done: number,
  failed: number
): ProgressTileState[] {
  const safeTotal = Math.max(0, total);
  const safeDone = Math.min(safeTotal, Math.max(0, done));
  const safeFailed = Math.min(safeDone, Math.max(0, failed));
  const making = status === 'making' ? Math.min(DIRECTED_CLIPS_AT_ONCE, safeTotal - safeDone) : 0;
  const queued = status === 'making' ? safeTotal - safeDone - making : 0;
  const ready = safeTotal - safeFailed - making - queued;
  return [
    ...Array<ProgressTileState>(ready).fill('ready'),
    ...Array<ProgressTileState>(safeFailed).fill('failed'),
    ...Array<ProgressTileState>(making).fill('making'),
    ...Array<ProgressTileState>(queued).fill('queued'),
  ];
}

/**
 * The picture of the tile of one clip: the still of a ready clip (else its keyframe), and the
 * keyframe of a clip in the making (the tile dims it). A queued clip and a failed clip show none, so
 * a picture at full strength always means a ready clip. Pure.
 */
export function tilePictureOf(entry: DirectedVideoClipEntry): string | null {
  const keyframe = entry.keyframeUrl ?? null;
  switch (entry.status) {
    case 'ready':
      return entry.stillUrl ?? keyframe;
    case 'making':
      return keyframe;
    case 'queued':
    case 'failed':
      return null;
  }
}

/** The clip counts of the make counter: the ready clips, the clips in the making, all clips. */
export interface MakeClipCounts {
  ready: number;
  making: number;
  total: number;
}

/**
 * The clip counts of the make counter. The counter counts the ready clips only, the same clips that
 * show a picture at full strength. The API answers the state of each clip (`clipEntries`). An older
 * API answers only `progress.clipsDone` (ready and failed clips): then the failed clips come off it,
 * and the clips in the making are a guess (`DIRECTED_CLIPS_AT_ONCE`). Pure.
 */
export function makeClipCounts(
  video: Pick<SerializedDirectedVideo, 'status' | 'progress' | 'clips' | 'clipEntries'>
): MakeClipCounts {
  const entries = video.clipEntries ?? [];
  if (entries.length > 0) {
    return {
      ready: entries.filter((entry) => entry.status === 'ready').length,
      making: entries.filter((entry) => entry.status === 'making').length,
      total: entries.length,
    };
  }
  const total = Math.max(0, video.progress?.clipsTotal ?? video.clips.total);
  const done = Math.min(total, Math.max(0, video.progress?.clipsDone ?? 0));
  const failed = Math.min(done, Math.max(0, video.clips.failed));
  const making = video.status === 'making' ? Math.min(DIRECTED_CLIPS_AT_ONCE, total - done) : 0;
  return { ready: done - failed, making, total };
}

/**
 * The tiles of the make, one for each Veo clip in script order. The API answers the state and the
 * still of each clip (`clipEntries`). An older API answers only the counts: then the tiles guess the
 * order (`progressTiles`) and show no still. Pure.
 */
export function makeTiles(
  video: Pick<SerializedDirectedVideo, 'status' | 'progress' | 'clips' | 'clipEntries'>
): ProgressTile[] {
  const entries = video.clipEntries ?? [];
  if (entries.length > 0) {
    return entries.map((entry) => ({
      key: entry.clipKey,
      state: entry.status,
      stillUrl: tilePictureOf(entry),
    }));
  }
  const total = video.progress?.clipsTotal ?? video.clips.total;
  const done = video.progress?.clipsDone ?? (video.status === 'making' ? 0 : total);
  return progressTiles(video.status, total, done, video.clips.failed).map((state, index) => ({
    key: `tile-${index}`,
    state,
    stillUrl: null,
  }));
}

/** A share in percent, 0 with no total. Pure. */
export function sharePercent(done: number, total: number): number {
  return total > 0 ? Math.round((Math.min(done, total) / total) * 100) : 0;
}
